import { FC, useCallback, useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import {
  approveVendorLogisticsChangeRequest,
  listVendorLogisticsChangeRequests,
  rejectVendorLogisticsChangeRequest,
  VendorLogisticsChangeRequest,
} from '../core/_requests'
import { useProcurementPermissions } from '../core/permissionAccess'
import { PROCUREMENT_PERMS } from '../core/permissions'
import VendorLogisticsChangeRequestModal from './VendorLogisticsChangeRequestModal'

const VendorLogisticsChangeReview: FC = () => {
  const { canAny } = useProcurementPermissions()
  const canReview = canAny([PROCUREMENT_PERMS.PO_VENDOR_LOGISTICS_REVIEW])
  const canView = canAny([
    PROCUREMENT_PERMS.PO_VENDOR_LOGISTICS_VIEW,
    PROCUREMENT_PERMS.PO_VENDOR_LOGISTICS_REVIEW,
  ])

  const [requests, setRequests] = useState<VendorLogisticsChangeRequest[]>([])
  const [loading, setLoading] = useState(true)
  const [activeRequest, setActiveRequest] = useState<VendorLogisticsChangeRequest | null>(null)
  const [actionLoading, setActionLoading] = useState(false)

  const loadRequests = useCallback(async () => {
    try {
      setLoading(true)
      const data = await listVendorLogisticsChangeRequests('PENDING')
      setRequests(data)
    } catch (error: any) {
      toast.error(error?.message || 'Failed to load logistics change requests')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (canView) {
      void loadRequests()
    } else {
      setLoading(false)
    }
  }, [canView, loadRequests])

  const handleApprove = async (requestId: number) => {
    try {
      setActionLoading(true)
      await approveVendorLogisticsChangeRequest(requestId)
      toast.success('Logistics pricing change approved')
      setActiveRequest(null)
      await loadRequests()
    } catch (error: any) {
      toast.error(error?.message || 'Failed to approve request')
    } finally {
      setActionLoading(false)
    }
  }

  const handleReject = async (requestId: number, reason: string) => {
    try {
      setActionLoading(true)
      await rejectVendorLogisticsChangeRequest(requestId, reason)
      toast.success('Logistics pricing change rejected')
      setActiveRequest(null)
      await loadRequests()
    } catch (error: any) {
      toast.error(error?.message || 'Failed to reject request')
    } finally {
      setActionLoading(false)
    }
  }

  if (!canView) {
    return <div className='alert alert-warning m-6'>You do not have access to vendor logistics change requests.</div>
  }

  return (
    <div className='card m-6'>
      <div className='card-header'>
        <h3 className='card-title mb-0'>Vendor Logistics Pricing Requests</h3>
      </div>
      <div className='card-body'>
        {loading ? (
          <div>Loading...</div>
        ) : requests.length === 0 ? (
          <div className='text-muted'>No pending logistics pricing requests.</div>
        ) : (
          <div className='table-responsive'>
            <table className='table table-row-bordered align-middle'>
              <thead>
                <tr>
                  <th>Request ID</th>
                  <th>RFQ</th>
                  <th>Requisition</th>
                  <th>PO</th>
                  <th>Vendor</th>
                  <th>Submitted At</th>
                  <th>Current Total</th>
                  <th>Requested Total</th>
                  <th>Difference</th>
                  <th>Status</th>
                  {canReview && <th className='text-end'>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {requests.map((request) => (
                  <tr key={request.id}>
                    <td>{request.id}</td>
                    <td>{request.rfqCode || request.rfqId || '-'}</td>
                    <td>{request.requisitionCode || request.requisitionId || '-'}</td>
                    <td>{request.poNumber || request.purchaseOrderId || '-'}</td>
                    <td>{request.vendorName || request.vendorId || '-'}</td>
                    <td>{request.requestedAt ? new Date(request.requestedAt).toLocaleString() : '-'}</td>
                    <td>{request.oldQuotationTotal ?? '-'}</td>
                    <td>{request.requestedQuotationTotal ?? '-'}</td>
                    <td>{request.totalDifference ?? '-'}</td>
                    <td>{request.status}</td>
                    {canReview && (
                      <td className='text-end'>
                        <button
                          type='button'
                          className='btn btn-sm btn-light-primary'
                          disabled={actionLoading}
                          onClick={() => setActiveRequest(request)}
                        >
                          Review
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <VendorLogisticsChangeRequestModal
        open={activeRequest != null}
        request={activeRequest}
        canReview={canReview}
        submitting={actionLoading}
        onClose={() => setActiveRequest(null)}
        onApprove={handleApprove}
        onReject={handleReject}
      />
    </div>
  )
}

export default VendorLogisticsChangeReview
